import { Box, styled } from "@mui/material";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ActionBar from "./action/ActionBar";
import Toolbar from "./tool/Toolbar";
import { useTranslation } from "react-i18next";
import frLocale from "date-fns/locale/fr";
import enUsLocale from "date-fns/locale/en-US";
import RecurrenceSelect, { NO_RECURRENCE } from "../RecurrenceSelect";

function getRandomAestheticColor(): string {
    const goldenRatio = 0.618033988749895;
    let hue = (Math.random() + goldenRatio) % 1;
    const minLightness = 0.4;
    const maxLightness = 0.8;
    const saturation = 0.7;
    let lightness =
        Math.random() * (maxLightness - minLightness) + minLightness;

    hue = Math.floor(hue * 360);
    const color = `hsl(${hue}, ${saturation * 100}%, ${lightness * 100}%)`;
    return color;
}

interface CustomDayProps<TDate extends Date> extends PickersDayProps<TDate> {
    selectedDays: TDate[];
}

const colors: Map<string, string> = new Map();

export const getColor = (date: Date) => {
    const dateFormated = moment(date).format("YYYY/MM/DD");

    if (colors.has(dateFormated)) {
        return colors.get(dateFormated) as string;
    }

    const color = getRandomAestheticColor();
    colors.set(dateFormated, color);

    return color;
};

const isDaySelected = (selectedDays: Date[], day: Date) =>
    selectedDays.some((selectedDay) => moment(selectedDay).isSame(day, "day"));

const CustomDay = ({
    selectedDays,
    day,
    ...other
}: CustomDayProps<Date>): any => {
    return (
        <PickersDay
            day={day}
            sx={{
                '&.Mui-selected': {
                    color: (theme) => theme.palette.getContrastText(getColor(day)),
                    backgroundColor: getColor(day)
                },
                color: (theme) =>
                    isDaySelected(selectedDays, day)
                        ? theme.palette.getContrastText(getColor(day))
                        : theme.palette.text.primary,
                backgroundColor: (theme) =>
                    isDaySelected(selectedDays, day)
                        ? getColor(day)
                        : theme.palette.background.paper,
                fontWeight: (theme) => (isDaySelected(selectedDays, day) ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular)
            }}
            {...other}
        />
    );
};

const ToolbarContainer = styled(Box)(() => ({
    paddingTop: 2,
    maxWidth: 400,
    height: 325,
}));

const CalendarContainer = styled(Box)(() => ({
    borderRadius: 1,
    justifyContent: "space-around",
}));

interface MultipleDatePickerProps {
    onConfirm?: (dates: Date[]) => void;
    selectedDates: Date[];
    setSelectedDates: Dispatch<SetStateAction<Date[]>>;
    scheduledDates: Date[];
    setScheduledDates: Dispatch<SetStateAction<Date[]>>;
    maxDate?: Date;
    disablePast?: boolean;
    isDisabled?: boolean;
    hasRecurrence?: boolean;

}
const MultipleDatePicker = ({
    onConfirm,
    selectedDates,
    setSelectedDates,
    scheduledDates,
    setScheduledDates,
    maxDate,
    disablePast,
    isDisabled,
    hasRecurrence,
}: MultipleDatePickerProps) => {
    const { i18n } = useTranslation();
    const [defaultRecurrenceOption, setDefaultRecurrenceOption] = useState(NO_RECURRENCE);
    const [manuallySelectedDates, setManuallySelectedDates] = useState<Date[]>([]);
    const [postRecurrenceDates, setPostRecurrenceDates] = useState<Date[]>([]);

    const daySlotProps = {
        day: {
            selectedDays: selectedDates,
        },
    } as any;
    const fromDateToKey = (date: Date) => {
        const keyFormat: string = "YYYY/MM/DD";
        return moment(date).format(keyFormat);
    };


    const updateDates = (newValue: Date | null) => {
        if (!newValue) return;

        // Standardize to start of day to avoid time-mismatch bugs
        const cleanDate = moment(newValue).startOf('day').toDate();

        // If hasRecurrence is false (Rescheduling mode), we just want one date
        if (!hasRecurrence) {
            setSelectedDates([cleanDate]);
            setManuallySelectedDates([cleanDate]);
            return;
        }

        // Otherwise, keep your existing toggle logic for recurrence
        const addSelectedDates = (old: Date[]) => {
            const dateKey = fromDateToKey(cleanDate);
            const index = old.map(fromDateToKey).indexOf(dateKey);
            if (index >= 0) return old.filter((_, i) => i !== index);
            return [...old, cleanDate];
        };

        setSelectedDates(addSelectedDates);
        setManuallySelectedDates(addSelectedDates);
    };


    const actionBarPops = {
        onClear: () => {
            setScheduledDates([])
            setSelectedDates([])
            setManuallySelectedDates([])
            setPostRecurrenceDates([])
            setDefaultRecurrenceOption(NO_RECURRENCE)
            colors.clear()
        },
        onConfirm: onConfirm ? () => onConfirm(selectedDates) : undefined,
    };

    useEffect(() => {
        setManuallySelectedDates(selectedDates);
        setPostRecurrenceDates(selectedDates);
        return colors.clear();
    }, []);

    return (
        <LocalizationProvider
            adapterLocale={i18n.language === "fr" ? frLocale : enUsLocale}
            dateAdapter={AdapterDateFns}
        >
            <Box
                sx={{
                    width: "100%",

                    height: "100%",
                    backgroundColor: (theme) => theme.palette.background.paper,
                }}
            >
                <CalendarContainer sx={{ display: { xs: "grid", md: "flex" }, }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Box>
                            <DateCalendar
                                // FIX: This key forces the calendar to re-render and focus on the correct month
                                key={selectedDates[0] ? selectedDates[0].toISOString() : 'initial'}
                                disabled={isDisabled}
                                disablePast={disablePast}
                                maxDate={maxDate || moment().add(5, 'years').toDate()}
                                // Ensure value is a single Date object for the UI to highlight it
                                value={selectedDates[0] || null}
                                onChange={updateDates}
                                slots={{ day: CustomDay as any }}
                                slotProps={{
                                    day: {
                                        selectedDays: selectedDates,
                                    } as any,
                                }}
                            />
                        </Box>
                        {hasRecurrence && <RecurrenceSelect
                            defaultRecurrenceOption={defaultRecurrenceOption}
                            setDefaultRecurrenceOption={setDefaultRecurrenceOption}
                            setSelectedDates={setSelectedDates}
                            manuallySelectedDates={manuallySelectedDates}
                            postRecurrenceDates={postRecurrenceDates}
                            scheduledDates={scheduledDates}
                            colors={colors}
                        />}
                    </Box>

                    <ToolbarContainer sx={{ minWidth: { sx: 200, md: 350 } }} >
                        <Toolbar dates={selectedDates} onDelete={updateDates} />
                    </ToolbarContainer>
                </CalendarContainer>

                <ActionBar {...actionBarPops} />
            </Box>
        </LocalizationProvider >
    );
};

export default MultipleDatePicker;
